import React, { useState } from 'react'
import TokTokDocForm from '../../Common/Form'
import PlannedConsultationEditorForm from './PlannedConsultationEditorForm'
import useStyles from './_styles'
import { getIsDateOverdue } from '../../../utils/dates'
import { ConsultationFormData, ConsultationFormErrorState, PlannedConsultationEditorProps } from './_props'

const PlannedConsultationEditor = ({
    consultationDetail,
    isNewConsultation,
    onSave,
    children,
}: PlannedConsultationEditorProps) => {
    const classes = useStyles()
    const [isSaveEnabled, setIsSaveEnabled] = React.useState(false)
    const [isSaving, setIsSaving] = React.useState(false)
    const [consultation, setConsultation] = useState<ConsultationFormData>(consultationDetail)
    const [errors, setErrors] = useState<ConsultationFormErrorState>({
        appointmentDate: false,
        overdueDate: false,
    })
    const [shouldBlockNavigation, seShouldBlockNavigation] = React.useState(false)

    React.useEffect(() => {
        const hasPatient = !['', undefined, null].includes(consultation.patientId)
        const hasRq = !['', undefined, null].includes(consultation.requiredExpertId)
        const hasDate = !['', undefined, null].includes(consultation.appointmentDate)

        if (hasPatient && hasRq && hasDate && errors.appointmentDate === false) {
            setIsSaveEnabled(true)
        } else {
            setIsSaveEnabled(false)
        }
    }, [consultation, errors])

    React.useEffect(() => {
        if (consultation.patientId || consultation.requiredExpertId || consultation.appointmentDate) {
            seShouldBlockNavigation(true)
        } else {
            seShouldBlockNavigation(false)
        }
    }, [consultation.patientId, consultation.requiredExpertId, consultation.appointmentDate])

    React.useEffect(() => {
        const init = async () => {
            if (consultation.appointmentDate) {
                const isOverdue = getIsDateOverdue(new Date(consultation.appointmentDate))

                // @ts-expect-error TS(2345) FIXME: Argument of type '(prevState: { appointmentDate: b... Remove this comment to see the full error message
                setErrors((prevState) => ({
                    ...prevState,
                    overdueDate: isOverdue,
                }))
            }
        }
        init()
    }, [consultation.appointmentDate])

    const handlePatientSelect = (value: any) => {
        setConsultation({
            ...consultation,
            patientId: value?.id || '',
            patientLibelle: value?.libelle || '',
        })
    }

    const handleRequiredExpertSelect = (value: any) => {
        setConsultation({
            ...consultation,
            requiredExpertId: value?.id || '',
            requiredExpertLibelle: value?.libelle || '',
        })
    }

    const handleAppointementDateChange = async (date: any) => {
        if (date !== consultation.appointmentDate) {
            const values = {
                ...consultation,
                appointmentDate: date,
            }

            setConsultation(values)
            if (!isNewConsultation) await onSave(values)
        }
    }

    const handleFormChange = (type: any, data: any) => {
        if (type === 'patient') {
            handlePatientSelect(data)
        }
        if (type === 'requiredExpert') {
            handleRequiredExpertSelect(data)
        }
        if (type === 'date') {
            handleAppointementDateChange(data)
        }
    }

    const handleSave = async () => {
        setIsSaving(true)
        onSave(consultation).finally(() => setIsSaving(false))
    }

    return (
        <TokTokDocForm
            className={classes.PlannedConsultationEditorTokTokDocForm}
            edition={isNewConsultation}
            displaySaveComponent={isNewConsultation}
            onSave={handleSave}
            allowDelete={false}
            isSaveEnabled={() => isSaveEnabled && !isSaving}
            cancelRedirectTo="/planned-consultations"
            allowedRoles="USER"
            shouldBlockNavigation={() => shouldBlockNavigation}
        >
            <div className={classes.PlannedConsultationEditor}>
                <PlannedConsultationEditorForm
                    isAutoSaveEnabled={!isNewConsultation}
                    consultation={consultation}
                    onChange={handleFormChange}
                    errors={errors}
                    setErrors={setErrors}
                />
                {children}
            </div>
        </TokTokDocForm>
    )
}

export default PlannedConsultationEditor
